import { Link } from "react-router-dom";
import { LegalLayout } from "@/components/legal/LegalLayout";
import { LegalP, LegalSection, LegalUl } from "@/components/legal/LegalProse";
import { OperatorContact } from "@/components/legal/OperatorContact";
import { OPERATOR } from "@/lib/legal";
import { tw } from "@/lib/theme";

export function PrivacyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      subtitle={`How ${OPERATOR.product} collects, uses, and protects your information.`}
    >
      <LegalSection title="1. Who we are">
        <LegalP>
          {OPERATOR.product} is operated by <strong>{OPERATOR.name}</strong>, based in
          Andhra Pradesh, India. For privacy questions or to exercise your rights, contact us
          using the details at the end of this policy or on our{" "}
          <Link to="/contact" className={tw.link}>
            Contact
          </Link>{" "}
          page.
        </LegalP>
        <OperatorContact />
      </LegalSection>

      <LegalSection title="2. Scope">
        <LegalP>
          This policy applies to the {OPERATOR.product} Android app, website at daywia.com,
          and related services (feeds, chat, Drifts, Parallel, payments, and invites). By creating
          an account you agree to this policy and our processing under India&apos;s Digital
          Personal Data Protection Act, 2023 (DPDP Act) where applicable.
        </LegalP>
      </LegalSection>

      <LegalSection title="3. Information we collect">
        <LegalUl
          items={[
            "Account: email, password (hashed by our auth provider), display name, bio, profile photo, mobile number (for contact sync and friend discovery), language and appearance preferences.",
            "Content you create: Frames, Drifts, Parallel sessions, captions, comments, reactions, and audience settings (Public or Contacts).",
            "Contacts (optional): if you grant permission, we process contact names and phone numbers from your address book. Phone numbers are hashed for matching; we do not publish your contact list.",
            "Messaging: chat messages and shared media between you and other users.",
            "Device & usage: device tokens for push notifications, app version, coarse usage signals for feed ranking and security.",
            "Payments: when you tip a creator, subscribe, or unlock paid content, transaction references are processed by Cashfree Payments; we store payment and subscription records on our servers (no virtual currency wallet).",
            "Ads: Google AdMob may collect advertising identifiers and interaction data per Google’s policies when ads are shown.",
          ]}
        />
      </LegalSection>

      <LegalSection title="4. How we use information">
        <LegalUl
          items={[
            "Provide and secure the service: authentication, feeds, chat, uploads, notifications, and account recovery.",
            "Contact graph: match hashed phone numbers so you can find friends who use Daywia (with your consent).",
            "Creator features: tips, subscriptions, paid unlocks, Sparks, and promotions where enabled.",
            "Safety: detect spam, abuse, and policy violations; respond to reports.",
            "Legal compliance: record consent, honour export and deletion requests, and meet applicable law.",
          ]}
        />
      </LegalSection>

      <LegalSection title="5. Legal bases (DPDP Act)">
        <LegalP>
          We process personal data based on your consent (signup checkboxes, contact sync
          permission, optional marketing), performance of our contract with you (providing the
          app), and legitimate uses such as security and fraud prevention. You may withdraw
          consent where applicable without affecting prior lawful processing.
        </LegalP>
      </LegalSection>

      <LegalSection title="6. Retention">
        <LegalP>
          Unless you delete your account sooner, we generally retain account, feed, and message
          data for up to <strong>12 months</strong> from your last activity, as disclosed at
          signup. Media on our CDN (Cloudflare R2) is removed when you delete posts or your
          account. Backup and log data may persist briefly for security and operations.
        </LegalP>
      </LegalSection>

      <LegalSection title="7. Sharing">
        <LegalUl
          items={[
            "Infrastructure: Supabase (database, auth, edge functions), Cloudflare (media CDN), Firebase (push), Cashfree Payments (payments), Google (AdMob ads).",
            "Other users: content you post publicly or to contacts is visible per your audience settings; profile information is shown as you configure.",
            "Legal: we may disclose information if required by law or to protect users and the platform.",
            "We do not sell your personal data.",
          ]}
        />
      </LegalSection>

      <LegalSection id="delete" title="8. Your rights">
        <LegalP>
          You may access, correct, or delete your data. Export and account deletion are available
          in the app (Settings → FAQ → Delete account) and described on our{" "}
          <Link to="/data-deletion" className={tw.link}>
            Data deletion
          </Link>{" "}
          page. To lodge a grievance under the DPDP Act, email{" "}
          <a href="mailto:support@daywia.com" className={tw.link}>
            support@daywia.com
          </a>{" "}
          or{" "}
          <a href="mailto:help@daywia.com" className={tw.link}>
            help@daywia.com
          </a>
          . We aim to respond within 30 days.
        </LegalP>
      </LegalSection>

      <LegalSection title="9. Children">
        <LegalP>
          {OPERATOR.product} is not directed at children under 13. We do not knowingly collect
          data from children. See our{" "}
          <Link to="/child-safety" className={tw.link}>
            Child safety
          </Link>{" "}
          policy to report concerns.
        </LegalP>
      </LegalSection>

      <LegalSection title="10. Security">
        <LegalP>
          We use encryption in transit (TLS), access controls, row-level security on our
          database, and hashed contact matching. No method is 100% secure; please use a strong
          password and report suspicious activity.
        </LegalP>
      </LegalSection>

      <LegalSection title="11. Changes">
        <LegalP>
          We may update this policy. Material changes will be reflected on this page with a new
          “Last updated” date. Continued use after changes constitutes acceptance where permitted
          by law.
        </LegalP>
      </LegalSection>
    </LegalLayout>
  );
}
